import React, { useState, useEffect } from 'react';
import { useData } from '../../hooks/useData';
import { useToast } from '../../hooks/useToast';

function BioequivalenceParallel() {
  const { state, upsert, audit, currentUser } = useData();
  const toast = useToast();
  
  const [inputs, setInputs] = useState({
    gmr: 0.95,
    cvTotal: 35,
    lower: 0.80,
    upper: 1.25,
    alpha: 0.05,
    power: 0.80,
    alloc: 1,
    dropout: 10
  });

  const [result, setResult] = useState(null);
  const [scenarioName, setScenarioName] = useState('Parallel BE — ' + new Date().toLocaleDateString());
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [source, setSource] = useState('');
  const [editableJustification, setEditableJustification] = useState('');
  const [activeTab, setActiveTab] = useState('results');
  const [sensitivityResults, setSensitivityResults] = useState(null);
  const [powerCurveData, setPowerCurveData] = useState(null);
  const [sampleSizeCurveData, setSampleSizeCurveData] = useState(null);

  // Statistical engine
  const Stat = {
    pnorm(x) {
      if (!isFinite(x)) return x > 0 ? 1 : 0;
      const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741, a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
      const sign = x < 0 ? -1 : 1;
      const ax = Math.abs(x) / Math.SQRT2;
      const t = 1.0 / (1.0 + p * ax);
      const y = 1.0 - (((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-ax * ax));
      return 0.5 * (1.0 + sign * y);
    },
    qnorm(p) {
      if (p <= 0) return -Infinity;
      if (p >= 1) return Infinity;
      const a = [-3.969683028665376e+01, 2.209460984245205e+02, -2.759285104469687e+02, 1.383577518672690e+02, -3.066479806614716e+01, 2.506628277459239e+00];
      const b = [-5.447609879822406e+01, 1.615858368580409e+02, -1.556989798598866e+02, 6.680131188771972e+01, -1.328068155288572e+01];
      const c = [-7.784894002430293e-03, -3.223964580411365e-01, -2.400758277161838e+00, -2.549732539343734e+00, 4.374664141464968e+00, 2.938163982698783e+00];
      const d = [7.784695709041462e-03, 3.224671290700398e-01, 2.445134137142996e+00, 3.754408661907416e+00];
      const plow = 0.02425, phigh = 1 - plow;
      let q, r;
      if (p < plow) {
        q = Math.sqrt(-2 * Math.log(p));
        return (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) / ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
      } else if (p <= phigh) {
        q = p - 0.5;
        r = q * q;
        return (((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q / (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1);
      } else {
        q = Math.sqrt(-2 * Math.log(1 - p));
        return -(((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) / ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
      }
    },
    qt(p, df) {
      if (df > 1000) return this.qnorm(p);
      const z = this.qnorm(p);
      const z2 = z * z;
      return z + (z * (z2 + 1)) / (4 * df) + (z * ((5 * z2 + 16) * z2 + 3)) / (96 * df * df) + (z * (((3 * z2 + 19) * z2 + 17) * z2 - 15)) / (384 * df * df * df);
    }
  };

  // Main calculation
  const calculate = () => {
    if (inputs.cvTotal <= 0 || inputs.gmr <= 0) {
      toast('GMR and total CV% must be positive.', 'warn');
      return;
    }

    const k = inputs.alloc > 0 ? inputs.alloc : 1;
    const U = Math.log(inputs.upper);
    const L = Math.log(inputs.lower);
    const lg = Math.log(inputs.gmr);

    if (lg <= L || lg >= U) {
      toast('Assumed GMR lies on/outside the BE limits; design infeasible.', 'warn');
      return;
    }

    const sigma = Math.sqrt(Math.log(1 + Math.pow(inputs.cvTotal / 100, 2)));
    const sigma2 = sigma * sigma;
    const delta = Math.min(U - lg, lg - L);

    // Variance of LSMean difference: Var = sigma^2 * (1+k)^2 / (k * N)
    const varFactor = (N) => sigma2 * Math.pow(1 + k, 2) / (k * N);
    const dfFn = (N) => N - 2;
    const roundBase = (k === 1) ? 2 : 1;
    const nStart = Math.max(4, roundBase * 2);

    let N = nStart;
    for (let it = 0; it < 60; it++) {
      const v = varFactor(N);
      if (!isFinite(v) || v <= 0) return null;
      const df = Math.max(1, dfFn(N));
      const ta = Stat.qt(1 - inputs.alpha, df);
      const tb = Stat.qt(inputs.power, df);
      const vTarget = (delta * delta) / Math.pow(ta + tb, 2);

      let Nnew = N;
      if (v > vTarget) {
        while (true) {
          Nnew += roundBase;
          if (varFactor(Nnew) <= vTarget) break;
          if (Nnew > 100000) return null;
        }
      } else {
        while (Nnew - roundBase >= nStart && varFactor(Nnew - roundBase) <= vTarget) {
          Nnew -= roundBase;
        }
      }
      if (Nnew === N) break;
      N = Nnew;
    }

    const nR = Math.ceil(N / (1 + k));
    const nT = N - nR;
    const se = Math.sqrt(varFactor(N));
    const dfResidual = dfFn(N);

    const taFinal = Stat.qt(1 - inputs.alpha, Math.max(1, dfResidual));
    const t1 = (U - lg) / se - taFinal;
    const t2 = (lg - L) / se - taFinal;
    const achievedPower = Math.max(0, Math.min(1, Stat.pnorm(t1) + Stat.pnorm(t2) - 1));

    const dropoutRate = inputs.dropout / 100;
    const enrolledT = Math.ceil(nT / (1 - dropoutRate));
    const enrolledR = Math.ceil(nR / (1 - dropoutRate));

    const resultData = {
      nTest: nT,
      nRef: nR,
      nTotal: N,
      nEnrolledTest: enrolledT,
      nEnrolledRef: enrolledR,
      nEnrolledTotal: enrolledT + enrolledR,
      sigma: sigma,
      sigma2: sigma2,
      deltaLog: delta,
      se: se,
      dfResidual: dfResidual,
      achievedPower: achievedPower,
      alpha: inputs.alpha,
      dropout: inputs.dropout,
      allocRatio: k,
      lg: lg,
      U: U,
      L: L,
      formulaSummary: `SE² = σ²·(1+k)²/(k·N); N from TOST with σ = √(ln(1+CV²)) (parallel, total-CV log-scale)`,
      note: "Parallel-arm BE uses TOTAL log-scale variability, not intra-subject CV. Use this design only when crossover is infeasible (long half-life, carry-over risk, ethical/safety constraints)."
    };

    setResult(resultData);
    setEditableJustification(generateJustification(inputs, resultData));
    generateSensitivity(inputs, resultData);
    generatePowerCurve(inputs, resultData);
    generateSampleSizeCurve(inputs, resultData);
    toast('Calculation complete', 'ok');
  };

  // Generate detailed justification
  const generateJustification = (inputs, result) => {
    const lg = result.lg;
    const U = result.U;
    const L = result.L;
    const k = result.allocRatio;
    
    return `
      <h4>1. Study Context and Design</h4>
      <p>
        This bioequivalence sample-size calculation is for a <strong>parallel design</strong> 
        (2 arms: Test and Reference) comparing Test (T) and Reference (R) formulations.
        The study is designed to demonstrate average bioequivalence (ABE) within the regulatory 
        acceptance limits of <strong>[${inputs.lower}, ${inputs.upper}]</strong> on the log scale.
      </p>
      <p>
        The parallel design is used when <strong>crossover is not feasible</strong> due to:
      </p>
      <ul>
        <li><strong>Long half-life drugs</strong> where washout periods would be excessively long</li>
        <li><strong>Carry-over risk</strong> where treatment effects persist across periods</li>
        <li><strong>Ethical/safety constraints</strong> where subjects cannot receive both treatments</li>
        <li><strong>Irreversible treatments</strong> (e.g., surgical interventions)</li>
      </ul>
      <p>
        <strong>Design characteristics:</strong>
        <ul>
          <li><strong>Arms:</strong> 2 (Test and Reference, independent groups)</li>
          <li><strong>Allocation:</strong> ${k}:1 (Test:Reference)</li>
          <li><strong>Key disadvantage:</strong> Does NOT control for between-subject variability</li>
          <li><strong>Variance source:</strong> Total between-subject variability (NOT intra-subject CV)</li>
        </ul>
      </p>

      <h4>2. Statistical Hypothesis</h4>
      <p>
        Two one-sided tests (TOST) at <strong>α = ${inputs.alpha}</strong> each:
      </p>
      <ul>
        <li><strong>H₀:</strong> ln(T/R) ≤ ln(${inputs.lower}) OR ln(T/R) ≥ ln(${inputs.upper})</li>
        <li><strong>H₁:</strong> ln(${inputs.lower}) &lt; ln(T/R) &lt; ln(${inputs.upper})</li>
      </ul>
      <p>
        The study is powered at <strong>${(inputs.power * 100).toFixed(0)}%</strong> to demonstrate 
        bioequivalence under the assumed GMR of <strong>${inputs.gmr}</strong>.
      </p>

      <h4>3. Assumptions and Input Parameters</h4>
      <table style="width: 100%; font-size: 13px; border-collapse: collapse; margin: 10px 0;">
        <tbody>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px; font-weight: 500;">Parameter</td>
            <td style="padding: 6px 8px; font-weight: 500;">Value</td>
            <td style="padding: 6px 8px; font-weight: 500;">Justification / Source</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">Test/Reference GMR</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">${inputs.gmr}</td>
            <td style="padding: 6px 8px; color: var(--ink-3);">${source || 'Based on previous studies or literature'}</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">Total CV% (between-subject)</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">${inputs.cvTotal}%</td>
            <td style="padding: 6px 8px; color: var(--ink-3);">${source || 'Based on previous studies or literature'}</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">BE Limits</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">[${inputs.lower}, ${inputs.upper}]</td>
            <td style="padding: 6px 8px; color: var(--ink-3);">Regulatory standard (CDSCO/EMA/FDA)</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">Significance Level (α)</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">${inputs.alpha}</td>
            <td style="padding: 6px 8px; color: var(--ink-3);">Standard for TOST procedure</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">Target Power (1 − β)</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">${(inputs.power * 100).toFixed(0)}%</td>
            <td style="padding: 6px 8px; color: var(--ink-3);">Industry standard for BE studies</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">Allocation Ratio (T:R)</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">${k}:1</td>
            <td style="padding: 6px 8px; color: var(--ink-3);">Study design consideration</td>
          </tr>
          <tr>
            <td style="padding: 6px 8px;">Anticipated Dropout</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">${inputs.dropout}%</td>
            <td style="padding: 6px 8px; color: var(--ink-3);">Based on historical study data</td>
          </tr>
        </tbody>
      </table>

      <h4>4. Statistical Method &amp; Formula</h4>
      <p>
        The parallel design sample size is estimated using the TOST procedure on the log scale:
      </p>
      <div style="background: var(--surface-2); padding: 12px 16px; border-radius: 4px; margin: 10px 0; font-family: var(--mono); font-size: 13px; text-align: center; border-left: 4px solid var(--accent-3);">
        SE² = σ² · (1+k)² / (k·N)
      </div>
      <p>where:</p>
      <ul>
        <li><strong>σ</strong> = log-scale total standard deviation = √(ln(1 + (CV<sub>total</sub>/100)²))</li>
        <li><strong>k</strong> = allocation ratio (Test:Reference)</li>
        <li><strong>N</strong> = total number of subjects (both arms)</li>
        <li><strong>δ</strong> = distance to the nearer BE limit on the log scale</li>
      </ul>
      <p>
        <strong>Critical distinction:</strong> Parallel designs use <em>total variability</em> 
        (between-subject), NOT intra-subject variability. This typically requires much larger 
        sample sizes than crossover designs.
      </p>

      <h4>5. Calculation Steps</h4>
      <table style="width: 100%; font-size: 13px; border-collapse: collapse; margin: 10px 0;">
        <tbody>
          <tr style="border-bottom: 1px solid var(--line); background: var(--surface-2);">
            <td style="padding: 6px 8px; font-weight: 500;">Step</td>
            <td style="padding: 6px 8px; font-weight: 500;">Calculation</td>
            <td style="padding: 6px 8px; font-weight: 500;">Result</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">1. Convert CV to log-scale σ</td>
            <td style="padding: 6px 8px; font-family: var(--mono); font-size: 12px;">σ = √(ln(1 + (${inputs.cvTotal}/100)²))</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">σ = ${result.sigma.toFixed(4)}</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">2. Log-transform BE limits</td>
            <td style="padding: 6px 8px; font-family: var(--mono); font-size: 12px;">ln(${inputs.lower}) to ln(${inputs.upper})</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">[${L.toFixed(4)}, ${U.toFixed(4)}]</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">3. Log-transform GMR</td>
            <td style="padding: 6px 8px; font-family: var(--mono); font-size: 12px;">ln(${inputs.gmr})</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">${lg.toFixed(4)}</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">4. Distance to nearer BE limit</td>
            <td style="padding: 6px 8px; font-family: var(--mono); font-size: 12px;">δ = min(U − lg, lg − L)</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">${result.deltaLog.toFixed(4)}</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">5. Variance factor</td>
            <td style="padding: 6px 8px; font-family: var(--mono); font-size: 12px;">σ² · (1+${k})² / ${k}</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">${(result.sigma2 * Math.pow(1 + k, 2) / k).toFixed(4)}</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">6. Iterative N search</td>
            <td style="padding: 6px 8px; font-family: var(--mono); font-size: 12px;">N + ${k} until variance ≤ target</td>
            <td style="padding: 6px 8px; font-family: var(--mono);"><strong>N = ${result.nTotal}</strong></td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">7. Per arm allocation</td>
            <td style="padding: 6px 8px; font-family: var(--mono); font-size: 12px;">Test: ${k}·N/(1+${k}), Reference: N/(1+${k})</td>
            <td style="padding: 6px 8px; font-family: var(--mono);"><strong>T: ${result.nTest}, R: ${result.nRef}</strong></td>
          </tr>
          <tr>
            <td style="padding: 6px 8px;">8. Dropout inflation</td>
            <td style="padding: 6px 8px; font-family: var(--mono); font-size: 12px;">n / (1 − ${inputs.dropout}/100)</td>
            <td style="padding: 6px 8px; font-family: var(--mono);"><strong>T: ${result.nEnrolledTest}, R: ${result.nEnrolledRef}</strong></td>
          </tr>
        </tbody>
      </table>

      <h4>6. Interpretation of Results</h4>
      <p>
        A total of <strong>${result.nEnrolledTotal}</strong> subjects will be randomized 
        (${result.nEnrolledTest} Test, ${result.nEnrolledRef} Reference) to provide 
        <strong>${(result.achievedPower * 100).toFixed(1)}%</strong> power to demonstrate 
        average bioequivalence within [${inputs.lower}, ${inputs.upper}] under the assumed 
        GMR of ${inputs.gmr} and total CV of ${inputs.cvTotal}%.
      </p>
      <p>
        <strong>Important considerations for parallel design:</strong>
      </p>
      <ul>
        <li><strong>Larger sample size:</strong> Parallel designs typically require 2-3× more subjects than crossover designs</li>
        <li><strong>Total variability:</strong> Uses between-subject variability (typically larger than within-subject)</li>
        <li><strong>No within-subject control:</strong> Cannot estimate intra-subject variability</li>
        <li><strong>When to use:</strong> Only when crossover is truly infeasible</li>
      </ul>

      <h4>7. Sensitivity Analysis</h4>
      <p>
        The sample size is highly sensitive to the assumed total CV% and GMR. 
        A sensitivity analysis should be performed varying these parameters.
      </p>

      <h4>8. Limitations &amp; Review Note</h4>
      <p>
        Parallel designs are less efficient than crossover designs and should only be used 
        when crossover is not feasible. Final sample size should be confirmed using 
        specialized BE software (e.g., SAS/PowerTOST, nQuery, PASS).
      </p>
    `;
  };

  // Generate sensitivity analysis
  const generateSensitivity = (inputs, result) => {
    const params = [
      { id: 'cvTotal', label: 'Total CV%', values: [20, 25, 30, 35, 40, 45, 50, 55] },
      { id: 'gmr', label: 'GMR', values: [0.85, 0.88, 0.90, 0.93, 0.95, 0.97, 0.99, 1.00] },
      { id: 'power', label: 'Target Power', values: [0.70, 0.75, 0.80, 0.85, 0.90, 0.95] },
      { id: 'alloc', label: 'Allocation (T:R)', values: [1, 1.5, 2, 2.5, 3, 4] },
      { id: 'dropout', label: 'Dropout %', values: [0, 5, 10, 15, 20, 25, 30] }
    ];

    const results = params.map(param => {
      const rows = [];
      for (const v of param.values) {
        const testInputs = { ...inputs, [param.id]: v };
        const r = runCalculation(testInputs);
        if (r) {
          rows.push({
            paramValue: v,
            nTotal: r.nTotal,
            nEnrolled: r.nEnrolledTotal,
            power: r.achievedPower
          });
        }
      }
      return { ...param, rows };
    });

    setSensitivityResults(results);
  };

  const runCalculation = (inputs) => {
    const k = inputs.alloc > 0 ? inputs.alloc : 1;
    const U = Math.log(inputs.upper);
    const L = Math.log(inputs.lower);
    const lg = Math.log(inputs.gmr);

    if (lg <= L || lg >= U) return null;

    const sigma = Math.sqrt(Math.log(1 + Math.pow(inputs.cvTotal / 100, 2)));
    const sigma2 = sigma * sigma;
    const delta = Math.min(U - lg, lg - L);

    const varFactor = (N) => sigma2 * Math.pow(1 + k, 2) / (k * N);
    const dfFn = (N) => N - 2;
    const roundBase = (k === 1) ? 2 : 1;
    const nStart = Math.max(4, roundBase * 2);

    let N = nStart;
    for (let it = 0; it < 60; it++) {
      const v = varFactor(N);
      if (!isFinite(v) || v <= 0) return null;
      const df = Math.max(1, dfFn(N));
      const ta = Stat.qt(1 - inputs.alpha, df);
      const tb = Stat.qt(inputs.power, df);
      const vTarget = (delta * delta) / Math.pow(ta + tb, 2);

      let Nnew = N;
      if (v > vTarget) {
        while (true) {
          Nnew += roundBase;
          if (varFactor(Nnew) <= vTarget) break;
          if (Nnew > 100000) return null;
        }
      } else {
        while (Nnew - roundBase >= nStart && varFactor(Nnew - roundBase) <= vTarget) {
          Nnew -= roundBase;
        }
      }
      if (Nnew === N) break;
      N = Nnew;
    }

    const nR = Math.ceil(N / (1 + k));
    const nT = N - nR;
    const se = Math.sqrt(varFactor(N));
    const dfResidual = dfFn(N);
    const taFinal = Stat.qt(1 - inputs.alpha, Math.max(1, dfResidual));
    const t1 = (U - lg) / se - taFinal;
    const t2 = (lg - L) / se - taFinal;
    const achievedPower = Math.max(0, Math.min(1, Stat.pnorm(t1) + Stat.pnorm(t2) - 1));

    const dropoutRate = inputs.dropout / 100;
    const enrolledT = Math.ceil(nT / (1 - dropoutRate));
    const enrolledR = Math.ceil(nR / (1 - dropoutRate));

    return {
      nTotal: N,
      nEnrolledTotal: enrolledT + enrolledR,
      achievedPower: achievedPower
    };
  };

  const generatePowerCurve = (inputs, result) => {
    const points = [];
    const baseN = result.nTotal;
    const minN = Math.max(4, Math.round(baseN * 0.2));
    const maxN = Math.max(minN + 1, Math.round(baseN * 2.5));
    const steps = 30;
    const dx = (maxN - minN) / steps;

    const k = inputs.alloc > 0 ? inputs.alloc : 1;
    const U = Math.log(inputs.upper);
    const L = Math.log(inputs.lower);
    const lg = Math.log(inputs.gmr);
    const delta = Math.min(U - lg, lg - L);
    const sigma = Math.sqrt(Math.log(1 + Math.pow(inputs.cvTotal / 100, 2)));
    const sigma2 = sigma * sigma;
    const varFactor = (N) => sigma2 * Math.pow(1 + k, 2) / (k * N);
    const dfFn = (N) => N - 2;

    for (let i = 0; i <= steps; i++) {
      const N = Math.round(minN + dx * i);
      if (N < 2) continue;
      const se = Math.sqrt(varFactor(N));
      const df = Math.max(1, dfFn(N));
      const ta = Stat.qt(1 - inputs.alpha, df);
      const t1 = delta / se - ta;
      const power = Math.max(0, Math.min(1, Stat.pnorm(t1) + Stat.pnorm(t1) - 1));
      points.push({ N, power });
    }

    setPowerCurveData(points);
  };

  const generateSampleSizeCurve = (inputs, result) => {
    const points = [];
    const cvValues = [15, 20, 25, 30, 35, 40, 45, 50, 55, 60];

    for (const cv of cvValues) {
      const testInputs = { ...inputs, cvTotal: cv };
      const r = runCalculation(testInputs);
      if (r) {
        points.push({ cv: cv, nTotal: r.nTotal, nEnrolled: r.nEnrolledTotal });
      }
    }

    setSampleSizeCurveData(points);
  };

  // SVG Chart Generator
  const renderSVGChart = ({ data, xKey, yKey, xLabel, yLabel, width = 560, height = 280, targetValue = null }) => {
    if (!data || data.length < 2) return null;

    const margin = { top: 20, right: 30, bottom: 40, left: 50 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const xValues = data.map(d => d[xKey]);
    const yValues = data.map(d => d[yKey]);
    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);
    const yMin = Math.min(0, ...yValues);
    const yMax = Math.max(...yValues) * 1.1;

    const xScale = (v) => margin.left + ((v - xMin) / (xMax - xMin)) * chartWidth;
    const yScale = (v) => margin.top + chartHeight - ((v - yMin) / (yMax - yMin)) * chartHeight;

    let svg = `<svg viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" font-family="ui-sans-serif, system-ui, sans-serif" font-size="11">`;

    // Grid lines
    const yTicks = [0, 0.25, 0.5, 0.75, 1];
    for (const tick of yTicks) {
      const y = yScale(tick);
      svg += `<line x1="${margin.left}" x2="${width - margin.right}" y1="${y}" y2="${y}" stroke="var(--line)" stroke-width="0.5" stroke-dasharray="3,3"/>`;
    }

    // Axes
    svg += `<line x1="${margin.left}" x2="${width - margin.right}" y1="${margin.top + chartHeight}" y2="${margin.top + chartHeight}" stroke="var(--ink)" stroke-width="1"/>`;
    svg += `<line x1="${margin.left}" x2="${margin.left}" y1="${margin.top}" y2="${margin.top + chartHeight}" stroke="var(--ink)" stroke-width="1"/>`;

    // Y-axis labels
    for (const tick of yTicks) {
      const y = yScale(tick);
      svg += `<text x="${margin.left - 8}" y="${y + 4}" text-anchor="end" fill="var(--ink-3)" font-size="10">${tick.toFixed(2)}</text>`;
    }

    // X-axis labels
    const step = Math.max(1, Math.floor(data.length / 8));
    for (let i = 0; i < data.length; i += step) {
      const x = xScale(data[i][xKey]);
      svg += `<text x="${x}" y="${margin.top + chartHeight + 18}" text-anchor="middle" fill="var(--ink-3)" font-size="9">${data[i][xKey].toFixed(1)}</text>`;
    }

    // X-axis label
    svg += `<text x="${margin.left + chartWidth / 2}" y="${height - 4}" text-anchor="middle" fill="var(--ink-2)" font-size="10" font-weight="500">${xLabel}</text>`;

    // Y-axis label
    svg += `<text transform="translate(14,${margin.top + chartHeight / 2}) rotate(-90)" text-anchor="middle" fill="var(--ink-2)" font-size="10" font-weight="500">${yLabel}</text>`;

    // Data line
    let path = '';
    for (let i = 0; i < data.length; i++) {
      const x = xScale(data[i][xKey]);
      const y = yScale(data[i][yKey]);
      path += (i === 0 ? 'M' : 'L') + x + ',' + y;
    }
    svg += `<path d="${path}" fill="none" stroke="var(--accent-3)" stroke-width="2"/>`;

    // Data points
    for (const d of data) {
      const x = xScale(d[xKey]);
      const y = yScale(d[yKey]);
      svg += `<circle cx="${x}" cy="${y}" r="3" fill="var(--accent-3)"/>`;
    }

    // Target line
    if (targetValue !== null && targetValue > 0) {
      const y = yScale(targetValue);
      svg += `<line x1="${margin.left}" x2="${width - margin.right}" y1="${y}" y2="${y}" stroke="var(--warn)" stroke-width="1.5" stroke-dasharray="6,4"/>`;
      svg += `<text x="${width - margin.right - 4}" y="${y - 4}" text-anchor="end" fill="var(--warn)" font-size="9" font-weight="500">Target</text>`;
    }

    svg += `</svg>`;
    return svg;
  };

  const saveScenario = () => {
    if (!result) {
      toast('Calculate first before saving.', 'warn');
      return;
    }

    const scenario = {
      id: 'be-parallel-' + Date.now(),
      name: scenarioName,
      methodKey: 'be_parallel',
      inputs: { ...inputs },
      result: { ...result },
      justification: editableJustification,
      source: source,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: currentUser?.name || 'Unknown'
    };

    upsert('bioequivalenceScenarios', scenario);
    audit('BE_PARALLEL_CREATE', scenarioName, `GMR=${inputs.gmr}, CV=${inputs.cvTotal}%, N=${result.nEnrolledTotal}`, {
      scenarioId: scenario.id,
      userId: currentUser?.id
    });
    toast('Bioequivalence scenario saved!', 'ok');
    setSavedScenarios([scenario, ...savedScenarios]);
  };

  const loadScenario = (scenario) => {
    setInputs(scenario.inputs);
    setResult(scenario.result);
    setScenarioName(scenario.name);
    setEditableJustification(scenario.justification || generateJustification(scenario.inputs, scenario.result));
    setSource(scenario.source || '');
    generateSensitivity(scenario.inputs, scenario.result);
    generatePowerCurve(scenario.inputs, scenario.result);
    generateSampleSizeCurve(scenario.inputs, scenario.result);
    toast('Scenario loaded', 'ok');
  };

  const deleteScenario = (id) => {
    setSavedScenarios(savedScenarios.filter(s => s.id !== id));
    audit('BE_PARALLEL_DELETE', id, 'Scenario deleted', { scenarioId: id });
    toast('Scenario deleted', 'ok');
  };

  const resetToDefaults = () => {
    setInputs({
      gmr: 0.95,
      cvTotal: 35,
      lower: 0.80,
      upper: 1.25,
      alpha: 0.05,
      power: 0.80,
      alloc: 1,
      dropout: 10
    });
    setResult(null);
    setEditableJustification('');
    setSensitivityResults(null);
    setPowerCurveData(null);
    setSampleSizeCurveData(null);
    toast('Reset to defaults', 'ok');
  };

  const copyJustification = () => {
    if (!editableJustification) {
      toast('No justification to copy', 'warn');
      return;
    }
    const text = editableJustification.replace(/<[^>]+>/g, '');
    navigator.clipboard?.writeText(text).then(() => {
      toast('Justification copied to clipboard', 'ok');
    }).catch(() => {
      toast('Copy failed', 'err');
    });
  };

  const copySAPParagraph = () => {
    if (!result) {
      toast('Calculate first', 'warn');
      return;
    }
    const para = `A total of ${result.nEnrolledTotal} subjects (${result.nEnrolledTest} Test, ${result.nEnrolledRef} Reference) will be enrolled in a parallel design (assuming ${inputs.dropout}% dropout) to provide approximately ${(result.achievedPower * 100).toFixed(1)}% power to demonstrate average bioequivalence within [${inputs.lower}, ${inputs.upper}] for a Test/Reference GMR of ${inputs.gmr} and total CV of ${inputs.cvTotal}%, at a one-sided significance level of ${inputs.alpha}. This parallel design is used because crossover is not feasible (e.g., long half-life, carry-over risk). Final sample size should be confirmed with specialized BE software.`;
    navigator.clipboard?.writeText(para).then(() => {
      toast('SAP paragraph copied', 'ok');
    }).catch(() => {
      toast('Copy failed', 'err');
    });
  };

  useEffect(() => {
    if (state.bioequivalenceScenarios) {
      const parallelScenarios = state.bioequivalenceScenarios
        .filter(s => s.methodKey === 'be_parallel')
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setSavedScenarios(parallelScenarios);
    }
  }, [state.bioequivalenceScenarios]);

  return (
    <div className="bioequivalence-module">
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Bioequivalence / PK</div>
          <h1 className="page-title">Bioequivalence — Parallel Design (TOST)</h1>
          <p className="page-sub">
            Two-arm parallel design with TOST procedure. Used when crossover is infeasible 
            (long half-life, carry-over risk, ethical/safety constraints).
            <span className="tag tag-warn tag-dot" style={{ marginLeft: 8 }}>Pre-validation</span>
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-primary" onClick={calculate}>
            <i className="ti ti-calculator" /> Calculate
          </button>
          <button className="btn" onClick={saveScenario}>
            <i className="ti ti-save" /> Save
          </button>
          <button className="btn btn-ghost" onClick={resetToDefaults}>
            <i className="ti ti-refresh" /> Reset
          </button>
        </div>
      </div>

      <div className="alert warn" style={{ marginBottom: 16 }}>
        <i className="ti ti-alert-triangle" />
        <strong>Parallel Design Note:</strong> Uses <strong>TOTAL between-subject variability</strong>, 
        NOT intra-subject CV. Requires significantly larger sample sizes than crossover designs. 
        Only use when crossover is truly infeasible.
      </div>

      <div className="row mb-4">
        <div className="col" style={{ maxWidth: 440 }}>
          <div className="card card-pad">
            <div className="card-title">
              <i className="ti ti-settings" /> Input Parameters
            </div>

            <div className="field">
              <label className="field-label">Scenario Name</label>
              <input
                type="text"
                value={scenarioName}
                onChange={(e) => setScenarioName(e.target.value)}
                placeholder="Enter scenario name..."
              />
            </div>

            <div className="field">
              <label className="field-label">Assumed Test/Reference GMR</label>
              <input
                type="number"
                step="0.01"
                min="0.5"
                max="2"
                value={inputs.gmr}
                onChange={(e) => setInputs({ ...inputs, gmr: parseFloat(e.target.value) || 0.95 })}
              />
              <div className="field-hint">Geometric mean ratio (Test / Reference)</div>
            </div>

            <div className="field">
              <label className="field-label">Total CV (%) — Between-Subject</label>
              <input
                type="number"
                step="0.5"
                min="0"
                value={inputs.cvTotal}
                onChange={(e) => setInputs({ ...inputs, cvTotal: parseFloat(e.target.value) || 35 })}
              />
              <div className="field-hint">Total log-scale variability (NOT intra-subject CV)</div>
            </div>

            <div className="field-row">
              <div className="field">
                <label className="field-label">Lower BE Limit</label>
                <input
                  type="number"
                  step="0.005"
                  min="0.5"
                  max="1"
                  value={inputs.lower}
                  onChange={(e) => setInputs({ ...inputs, lower: parseFloat(e.target.value) || 0.80 })}
                />
              </div>
              <div className="field">
                <label className="field-label">Upper BE Limit</label>
                <input
                  type="number"
                  step="0.005"
                  min="1"
                  max="2"
                  value={inputs.upper}
                  onChange={(e) => setInputs({ ...inputs, upper: parseFloat(e.target.value) || 1.25 })}
                />
              </div>
            </div>

            <div className="field-row">
              <div className="field">
                <label className="field-label">Alpha (α)</label>
                <input
                  type="number"
                  step="0.005"
                  min="0.001"
                  max="0.5"
                  value={inputs.alpha}
                  onChange={(e) => setInputs({ ...inputs, alpha: parseFloat(e.target.value) || 0.05 })}
                />
                <div className="field-hint">One-sided significance level per TOST</div>
              </div>
              <div className="field">
                <label className="field-label">Target Power</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.5"
                  max="0.999"
                  value={inputs.power}
                  onChange={(e) => setInputs({ ...inputs, power: parseFloat(e.target.value) || 0.80 })}
                />
                <div className="field-hint">Desired study power (1 − β)</div>
              </div>
            </div>

            <div className="field-row">
              <div className="field">
                <label className="field-label">Allocation (T:R)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="5"
                  value={inputs.alloc}
                  onChange={(e) => setInputs({ ...inputs, alloc: parseFloat(e.target.value) || 1 })}
                />
                <div className="field-hint">Test : Reference ratio</div>
              </div>
              <div className="field">
                <label className="field-label">Dropout (%)</label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="95"
                  value={inputs.dropout}
                  onChange={(e) => setInputs({ ...inputs, dropout: parseFloat(e.target.value) || 10 })}
                />
              </div>
            </div>

            <div className="field" style={{ marginTop: 8 }}>
              <label className="field-label">Assumption Source</label>
              <textarea
                rows="2"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="e.g. Previous study data, literature, regulatory guidance..."
                style={{ fontSize: 12 }}
              />
              <div className="field-hint">Appears in the saved scenario justification</div>
            </div>
          </div>
        </div>

        <div className="col">
          {result ? (
            <div className="card card-pad">
              <div className="card-title">
                <i className="ti ti-chart-bar" /> Results
              </div>

              <div className="metric-strip" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 16 }}>
                <div className="metric-cell">
                  <div className="metric-label">Test (T)</div>
                  <div className="metric-value">{result.nTest}</div>
                  <div className="metric-foot">evaluable subjects</div>
                </div>
                <div className="metric-cell">
                  <div className="metric-label">Reference (R)</div>
                  <div className="metric-value">{result.nRef}</div>
                  <div className="metric-foot">evaluable subjects</div>
                </div>
                <div className="metric-cell" style={{ borderRight: 'none' }}>
                  <div className="metric-label">Total Enrolled</div>
                  <div className="metric-value" style={{ color: 'var(--ok)' }}>{result.nEnrolledTotal}</div>
                  <div className="metric-foot">with {result.dropout}% dropout</div>
                </div>
              </div>

              <div className="divider" />

              <div className="field-row-3">
                <div>
                  <div className="tiny muted uc">σ (total)</div>
                  <div className="sm bold">{result.sigma.toFixed(4)}</div>
                  <div className="tiny muted">(CV = {inputs.cvTotal}%)</div>
                </div>
                <div>
                  <div className="tiny muted uc">SE (log scale)</div>
                  <div className="sm bold mono">{result.se.toFixed(4)}</div>
                </div>
                <div>
                  <div className="tiny muted uc">Achieved Power</div>
                  <div className="sm bold" style={{ color: 'var(--ok)' }}>{(result.achievedPower * 100).toFixed(1)}%</div>
                  <div className="tiny muted">Target: {(inputs.power * 100).toFixed(0)}%</div>
                </div>
              </div>

              <div className="divider" />

              <div className="field-row-3">
                <div>
                  <div className="tiny muted uc">δ (log scale)</div>
                  <div className="sm bold mono">{result.deltaLog.toFixed(4)}</div>
                </div>
                <div>
                  <div className="tiny muted uc">Residual df</div>
                  <div className="sm bold mono">{result.dfResidual}</div>
                </div>
                <div>
                  <div className="tiny muted uc">Allocation (T:R)</div>
                  <div className="sm bold mono">{result.allocRatio}:1</div>
                </div>
              </div>

              <div className="alert info" style={{ marginTop: 12 }}>
                <div className="mono xs">{result.formulaSummary}</div>
              </div>

              {result.note && (
                <div className="alert warn" style={{ marginTop: 8 }}>
                  <i className="ti ti-info-circle" /> {result.note}
                </div>
              )}
            </div>
          ) : (
            <div className="card card-pad">
              <div className="empty">
                <i className="ti ti-calculator" />
                <div className="empty-title">Ready to calculate</div>
                <div className="tiny muted">Enter parameters and click "Calculate"</div>
              </div>
            </div>
          )}

          {savedScenarios.length > 0 && (
            <div className="card card-pad mt-3">
              <div className="card-title">
                <i className="ti ti-files" /> Saved Scenarios ({savedScenarios.length})
              </div>
              <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                {savedScenarios.map((s, idx) => (
                  <div key={s.id || idx} className="flex between center" style={{ padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
                    <div>
                      <div className="sm bold">{s.name}</div>
                      <div className="tiny muted">GMR={s.inputs.gmr}, CV={s.inputs.cvTotal}%, N={s.result.nEnrolledTotal}</div>
                    </div>
                    <div className="flex gap-2">
                      <button className="btn btn-xs btn-ghost" onClick={() => loadScenario(s)} title="Load scenario">
                        <i className="ti ti-eye" />
                      </button>
                      <button className="btn btn-xs btn-ghost" style={{ color: 'var(--danger)' }} onClick={() => deleteScenario(s.id)} title="Delete scenario">
                        <i className="ti ti-trash" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {result && (
        <div>
          <div className="tabs">
            <button 
              className={`tab ${activeTab === 'results' ? 'active' : ''}`}
              onClick={() => setActiveTab('results')}
            >
              <i className="ti ti-file-text" /> Justification
            </button>
            <button 
              className={`tab ${activeTab === 'formula' ? 'active' : ''}`}
              onClick={() => setActiveTab('formula')}
            >
              <i className="ti ti-math" /> Formula & Methodology
            </button>
            <button 
              className={`tab ${activeTab === 'sensitivity' ? 'active' : ''}`}
              onClick={() => setActiveTab('sensitivity')}
            >
              <i className="ti ti-chart-line" /> Sensitivity Analysis
            </button>
            <button 
              className={`tab ${activeTab === 'curves' ? 'active' : ''}`}
              onClick={() => setActiveTab('curves')}
            >
              <i className="ti ti-chart-areas" /> Power & Sample Size Curves
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'results' && (
              <div className="card card-pad">
                <div className="alert info" style={{ marginBottom: 14 }}>
                  <i className="ti ti-edit" /> Edit any sentence below — the calculated values table 
                  (above) is preserved separately for traceability.
                </div>
                <div 
                  className="justification" 
                  contentEditable="true"
                  dangerouslySetInnerHTML={{ __html: editableJustification }}
                  onInput={(e) => setEditableJustification(e.target.innerHTML)}
                  style={{ 
                    minHeight: 200,
                    padding: '12px 16px',
                    border: '1px solid var(--line)',
                    borderRadius: 4,
                    outline: 'none',
                    background: 'var(--surface)'
                  }}
                />
                <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-sm btn-ghost" onClick={copyJustification}>
                    <i className="ti ti-copy" /> Copy justification text
                  </button>
                  <button className="btn btn-sm btn-ghost" onClick={copySAPParagraph}>
                    <i className="ti ti-file-text" /> Copy SAP paragraph only
                  </button>
                  <button className="btn btn-sm btn-ghost" onClick={() => {
                    if (result) {
                      const just = generateJustification(inputs, result);
                      setEditableJustification(just);
                      toast('Justification reset to generated version', 'ok');
                    }
                  }}>
                    <i className="ti ti-refresh" /> Reset to generated
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'formula' && (
              <div className="card card-pad">
                <h3 style={{ fontFamily: 'var(--display)', marginBottom: 12 }}>Formula & Methodology</h3>
                
                <h4>Statistical Model</h4>
                <p>The parallel design uses the following ANOVA model for the log-transformed PK parameters:</p>
                <div style={{ background: 'var(--surface-2)', padding: '12px 16px', borderRadius: 4, margin: '10px 0', fontFamily: 'var(--mono)', fontSize: 13 }}>
                  Y<sub>ik</sub> = μ + T<sub>k</sub> + ε<sub>ik</sub>
                </div>
                <ul>
                  <li><strong>Y<sub>ik</sub></strong> = log-transformed PK parameter for subject i, treatment k</li>
                  <li><strong>μ</strong> = overall mean</li>
                  <li><strong>T<sub>k</sub></strong> = fixed treatment effect</li>
                  <li><strong>ε<sub>ik</sub></strong> = residual error (between-subject variability)</li>
                </ul>

                <h4>Sample Size Formula</h4>
                <div style={{ background: 'var(--surface-2)', padding: '12px 16px', borderRadius: 4, margin: '10px 0', fontFamily: 'var(--mono)', fontSize: 14, textAlign: 'center', borderLeft: '4px solid var(--accent-3)' }}>
                  SE² = σ² · (1+k)² / (k·N)
                </div>

                <h4>Variance Components</h4>
                <ul>
                  <li><strong>σ² = ln(1 + (CV<sub>total</sub>/100)²)</strong> — total variance on log scale</li>
                  <li><strong>k</strong> = allocation ratio (Test:Reference)</li>
                </ul>

                <div className="alert warn" style={{ marginTop: 12 }}>
                  <i className="ti ti-alert-triangle" />
                  <strong>Critical:</strong> Parallel designs use TOTAL variability, NOT intra-subject CV.
                  This is the key difference from crossover designs and results in larger sample sizes.
                </div>
              </div>
            )}

            {activeTab === 'sensitivity' && sensitivityResults && (
              <div className="card card-pad">
                <h3 style={{ fontFamily: 'var(--display)', marginBottom: 12 }}>Sensitivity Analysis</h3>
                <p className="muted" style={{ marginBottom: 16 }}>
                  Varying key assumptions shows how the sample size changes under alternative scenarios.
                </p>

                {sensitivityResults.map((param, idx) => (
                  <div key={idx} style={{ marginBottom: 20 }}>
                    <h4>{param.label}</h4>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="tbl">
                        <thead>
                          <tr>
                            <th>{param.label}</th>
                            <th className="num">Total Evaluable (N)</th>
                            <th className="num">Total Enrolled</th>
                            <th className="num">Achieved Power</th>
                          </tr>
                        </thead>
                        <tbody>
                          {param.rows.map((row, i) => (
                            <tr key={i} style={row.nTotal === result?.nTotal ? { background: 'var(--accent-soft)' } : {}}>
                              <td><strong>{row.paramValue}</strong></td>
                              <td className="num">{row.nTotal}</td>
                              <td className="num">{row.nEnrolled}</td>
                              <td className="num">{(row.power * 100).toFixed(1)}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}

                <div className="alert warn" style={{ marginTop: 12 }}>
                  <i className="ti ti-alert-triangle" />
                  <strong>Interpretation:</strong> Parallel design sample sizes are highly sensitive to CV%.
                  A 10% increase in CV can double the required sample size.
                </div>
              </div>
            )}

            {activeTab === 'curves' && (
              <div className="card card-pad">
                <h3 style={{ fontFamily: 'var(--display)', marginBottom: 12 }}>Power & Sample Size Curves</h3>

                {powerCurveData && powerCurveData.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <h4>Power vs. Sample Size</h4>
                    <div 
                      dangerouslySetInnerHTML={{ 
                        __html: renderSVGChart({
                          data: powerCurveData,
                          xKey: 'N',
                          yKey: 'power',
                          xLabel: 'Total Sample Size (N)',
                          yLabel: 'Power',
                          targetValue: inputs.power
                        }) 
                      }} 
                    />
                    <div className="tiny muted" style={{ marginTop: 4 }}>
                      Target power: {(inputs.power * 100).toFixed(0)}% (dashed line) · Current N = {result?.nTotal || 0}
                    </div>
                  </div>
                )}

                {sampleSizeCurveData && sampleSizeCurveData.length > 0 && (
                  <div>
                    <h4>Sample Size vs. Total CV%</h4>
                    <div 
                      dangerouslySetInnerHTML={{ 
                        __html: renderSVGChart({
                          data: sampleSizeCurveData,
                          xKey: 'cv',
                          yKey: 'nTotal',
                          xLabel: 'Total CV (%)',
                          yLabel: 'Total Sample Size (N)',
                          targetValue: result?.nTotal || null
                        }) 
                      }} 
                    />
                    <div className="tiny muted" style={{ marginTop: 4 }}>
                      Current CV: {inputs.cvTotal}% · Current N = {result?.nTotal || 0} (dashed line)
                    </div>
                    <div className="tiny muted">
                      <strong>Interpretation:</strong> Parallel designs require exponentially larger samples as CV increases.
                      Consider crossover design if feasible.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default BioequivalenceParallel;